import { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { Stack, DefaultButton, TextField, IconButton, Dialog, PrimaryButton, DetailsList, DialogType, DetailsListLayoutMode, SelectionMode, IDropdownOption, DialogFooter, Separator, TooltipHost, IColumn, FontIcon, ITextField, Checkbox, IDropdown } from '@fluentui/react'

import styles from './Admin.module.css'

import { AppStateContext } from '../../state/AppProvider'
import { Dropdown } from '@fluentui/react'

import {
  configurationList,
  templateList,
  libraryList,
  configurationUpdate,
  libraryUpdate,
  templateUpdate,
  libraryGenerate,
  templateGenerate,
  templateDelete,
  libraryDelete,
  approvalList,
  approvalUpdate,
  FieldItem,
  templateTest,
  getWhitelists
} from '../../api'
import React from 'react'

// Define object types
interface Library {
  id: string
  name: string
  url: string
}

interface Template {
  id: string
  name: string
  source: string
  webConfig: string
  status: string
  prompts: PromptItem[]
  fields: Field[]
  docxFile: string
}

interface PromptItem {
  title: string
  prompt: string
}

interface Field {
  name: string
  type: string
  mapping: FieldMapping
}

interface FieldMapping {
  // Simple list of values for fields like Region
  list: string[]          // Example: ["SEET", "SEMED", "EE", "CA"]

  parentField: string

  // Nested fields (like Region -> Countries mapping)
  nestedFields: {        // Maps regions to their corresponding countries
    [key: string]: string[]  // E.g., "SEMED": ["Egypt", "Jordan"]
  }
}

interface Approval {
  id: string
  library: string
  site: string
  fileName: string
  status: string
  timestamp: string
  sizeMb: number
}

interface Configuration {
  id: string
  name: string
  description: string
  value: string
}

type DataItem = Configuration | Library | Template | Approval;


const Admin = () => {
  const appStateContext = useContext(AppStateContext)

  // Started changing here

  const inputRefs = useRef<{ [key: string]: ITextField | null }>({});
  const dropdownRef = useRef<IDropdown>(null);



  const [dataType, setDataType] = useState<"General" | "Library" | "Template" | "Approval">("General");
  const [items, setItems] = useState<DataItem[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);
  const [fieldValues, setFieldValues] = useState<{ [key: string]: string }>({});
  const [selectedTemplateFields, setSelectedTemplateFields] = useState<FieldItem[]>([]);


  const [currentTestItem, setCurrentTestItem] = useState<Partial<Template> & { index?: number }>({});

  const [currentItem, setCurrentItem] = useState<Partial<Library & Template & Configuration & Approval> & { index?: number }>({});

  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleOpenDeleteAlertDialog = (index: number) => {
    setSelectedIndex(index);
    setShowDeleteAlertDialog(true);
  };

  const handleCloseDeleteAlertDialog = () => {
    setShowDeleteAlertDialog(false);
    setSelectedIndex(null);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);  // Reads the file as a full Data URL
    reader.onload = () => {
      if (typeof reader.result === "string") {
        setCurrentItem(prev => ({
          ...prev,
          docxFile: typeof reader.result === 'string' ? reader.result : undefined // Ensure docxFile is a string or undefined
        }));
      }
    };
    reader.onerror = error => {
      console.error("File reading error:", error);
    };
  };

  const [testResponseList, setTestResponseList] = useState<string[][]>([]);

  const handleTestClick = async () => {
    if (
      currentTestItem.id &&
      currentTestItem.name &&
      currentTestItem.source &&
      currentTestItem.prompts &&
      currentTestItem.fields
    ) {
      const response = await handleTestTemplate(currentTestItem as Template, fieldValues);

      if (Array.isArray(response)) {
        setTestResponseList(response); // Store the response in state
      } else {
        console.error("Unexpected response format");
      }
    } else {
      console.error("Incomplete template data");
    }
  };


  const handleTestTemplate = async (template: Template, fieldValues: { [key: string]: string }) => {
    try {
      const response = await templateTest(template, fieldValues)

      if (response.ok) {
        const responseData = await response.json()
        return responseData
      }
    } catch (error) {
      console.error("Error processing approval:", error);
    }
  };

  const isAllFieldsSelected = useMemo(() => {
    return selectedTemplateFields
      .filter((field) => field.type === "dropdown") // Validate only dropdown fields
      .every((field) => fieldValues[field.name]);
  }, [selectedTemplateFields, fieldValues]);

  // Source Dropdown

  const [sourceOptions, setSourceOptions] = useState<{ key: string; text: string }[]>([]);
  const indexesList = appStateContext?.state.indexesList;


  // Get indexes names from Indexes List and change the dropdown source options

  useEffect(() => {
    if (indexesList?.indexes_list) {
      const options = indexesList.indexes_list.map(value => ({
        key: value.key,
        text: value.text
      }));
      setSourceOptions(options);
    }
  }, [indexesList]); // Re-run when indexesList changes

  const handleApproval = async (approval_id: string, isApproved: string) => {
    try {
      const response = await approvalUpdate(isApproved, approval_id)

      if (response.ok) {
        await fetchData("Approval"); // Refresh the list after approval/decline
      } else {
        console.error("Failed to process approval.");
      }
    } catch (error) {
      console.error("Error processing approval:", error);
    }
  };


  const modalProps = {
    titleAriaId: 'labelId',
    subtitleAriaId: 'subTextId',
    isBlocking: true,
    styles: { main: { maxWidth: 450 } }
  }

  const deleteAlertDialogContentProps = {
    type: DialogType.close,
    title: 'Are you sure you want to delete this item?',
    closeButtonAriaLabel: 'Close',
    subText: 'This item will forever be lost.'
  }

  // Data type options
  const dataOptions: IDropdownOption[] = [
    { key: "General", text: "General" },
    { key: "Library", text: "Libraries" },
    { key: "Template", text: "Templates" },
    { key: "Approval", text: "Upload Approvals" }
  ];

  // Function to add TooltipHost dynamically to all fields
  const addTooltipsToColumns = (columns: IColumn[]): IColumn[] => {
    return columns.map((col: IColumn) => ({
      ...col,
      onRender: (item: Record<string, any>) => (
        <TooltipHost content={item[col.fieldName as keyof typeof item] || ""} calloutProps={{ gapSpace: 5 }}>
          <span style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
            width: "100%"
          }}>
            {item[col.fieldName as keyof typeof item]}
          </span>
        </TooltipHost>
      )
    }));
  };


  const columnsMap = {
    Library: addTooltipsToColumns([
      { key: "name", name: "Name", fieldName: "name", minWidth: 200, maxWidth: 250, isResizable: true },
      { key: "url", name: "URL", fieldName: "url", minWidth: 250, isResizable: true },
    ]),
    Template: addTooltipsToColumns([
      { key: "name", name: "Name", fieldName: "name", minWidth: 200, maxWidth: 250, isResizable: true },
      { key: "source", name: "Source", fieldName: "source", minWidth: 200, isResizable: true },
      { key: "status", name: "Status", fieldName: "status", minWidth: 100, maxWidth: 100, isResizable: true }
    ]),
    General: addTooltipsToColumns([
      { key: "name", name: "Name", fieldName: "name", minWidth: 200, maxWidth: 250, isResizable: true },
      { key: "description", name: "Description", fieldName: "description", minWidth: 200, isResizable: true },
      { key: "value", name: "Value", fieldName: "value", minWidth: 200, maxWidth: 300, isResizable: true },
    ]),
    Approval: addTooltipsToColumns([
      { key: "library", name: "Library", fieldName: "library", minWidth: 100, maxWidth: 150, isResizable: true },
      { key: "site", name: "Site", fieldName: "site", minWidth: 100, maxWidth: 150, isResizable: true },
      { key: "fileName", name: "File Name", fieldName: "fileName", minWidth: 100, isResizable: true },
      { key: "sizeMb", name: "Size (MB)", fieldName: "sizeMb", minWidth: 75, maxWidth: 75, isResizable: true },
      { key: "status", name: "Status", fieldName: "status", minWidth: 100, maxWidth: 100, isResizable: true }
    ]),
  };


  // Function to get the appropriate columns
  const getColumns = () => [
    ...columnsMap[dataType], // Get object-specific columns
    {
      key: "actions",
      name: "Actions",
      minWidth: 260,
      onRender: (item: DataItem) => renderActions(item),
    },
  ];
  // Render action buttons dynamically
  const renderActions = (item: DataItem) => {
    const index = items.findIndex(i => i === item);

    if (dataType === "Approval") {
      return (
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <PrimaryButton text="Approve" onClick={() => handleApproval(item.id, "Approved")} />
          <DefaultButton text="Decline" onClick={() => handleApproval(item.id, "Declined")} />
        </Stack>
      );
    } else if (dataType === "Template") {
      return (
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <DefaultButton text="Edit" onClick={() => openDialog(item, index)} />
          <DefaultButton text="Delete" onClick={() => handleOpenDeleteAlertDialog(index)} />
          <DefaultButton text="Test" onClick={() => openTestDialog(item, index)} />
        </Stack>
      );
    } else {
      return (
        <Stack horizontal tokens={{ childrenGap: 10 }}>
          <DefaultButton text="Edit" onClick={() => openDialog(item, index)} />
          <DefaultButton text="Delete" disabled={dataType == "General"} onClick={() => handleOpenDeleteAlertDialog(index)} />
        </Stack>
      );
    }
  };

  // Handle field selection
  const onFieldValueChange = (fieldName: string, selectedOption: any) => {
    setFieldValues((prev) => ({
      ...prev,
      [fieldName]: selectedOption.key,
    }));
  };

  const openTestDialog = (item: Partial<Template> = {}, index?: number) => {
    setCurrentTestItem({
      ...(item as Template),
      prompts: item.prompts || [],  // Ensure prompts array exists
      fields: item.fields || [],    // Ensure fields array exists
      index
    });
    setIsTestDialogOpen(true);
  };

  const closeTestDialog = () => {
    setIsTestDialogOpen(false);
    setCurrentTestItem({});
    setTestResponseList([])
  };

  // Example when opening dialog (safe handling)
  const openDialog = async (item: DataItem | {} = {}, index?: number) => {
    const templateData = await templateList();
    //HS: Bug de lista desaparecida 
    //setItems(templateData || []);
    setCurrentItem({});
    setCurrentItem({
      ...(item as Partial<Library & Template & Configuration & Approval>),
      prompts: (item as Template)?.prompts || [],  // Ensure prompts array exists
      index
    });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setCurrentItem({});
    setIsDialogOpen(false);
  };

  // Create item
  const createItem = async () => {
    try {
      if (dataType === "Library") {
        const response = await libraryGenerate(currentItem as Library)

        if (response.ok) {
          const responseData = await response.json()
          setItems([...items, responseData])
          await fetchData("Library") // Fetch data again to update the list
        }
      } else if (dataType === "Template") {
        const response = await templateGenerate(currentItem as Template)

        if (response.ok) {
          const responseData = await response.json()
          setItems([...items, responseData])
          await fetchData("Template") // Fetch data again to update the list
        }
      }
      closeDialog()

    } catch (error) {
      console.error('Error creating item:', error);
    }
  };

  // Update item function
  const updateItem = async () => {
    if (!currentItem.id || currentItem.index === undefined) {
      console.error('ID or index is missing.')
      return;
    }

    try {
      if (dataType === "General") {
        const response = await configurationUpdate(currentItem as Configuration, currentItem.id);

        if (response.ok) {
          const responseData = await response.json();

          // Ensure the updated item has the correct structure
          const updatedItem: Configuration = {
            id: responseData.id, // Ensure 'id' is correctly set
            name: responseData.name, // Ensure 'name' is correctly set
            description: responseData.description,
            value: responseData.value, // Ensure 'value' is correctly set
          };

          // Update the state by creating a new array to trigger re-render
          setItems(prevItems =>
            prevItems.map(item =>
              item.id === updatedItem.id ? updatedItem : item
            )
          );
          await fetchData("General") // Fetch data again to update the list
        } else {
          console.error('Failed to update item', response.status);
        }
      } else if (dataType === "Library") {
        const response = await libraryUpdate(currentItem as Library, currentItem.id)

        if (response.ok) {
          const responseData = await response.json();

          // Ensure the updated item has the correct structure
          const updatedItem: Library = {
            id: responseData.id, // Ensure 'id' is correctly set
            name: responseData.name, // Ensure 'name' is correctly set
            url: responseData.url // Ensure 'url' is correctly set
          };

          // Update the state by creating a new array to trigger re-render
          setItems(prevItems =>
            prevItems.map(item =>
              item.id === updatedItem.id ? updatedItem : item
            )
          );
          await fetchData("Library") // Fetch data again to update the list
        }
      } else if (dataType === "Template") {
        const response = await templateUpdate(currentItem as Template, currentItem.id)

        if (response.ok) {
          const responseData = await response.json();

          // Ensure the updated item has the correct structure
          const updatedItem: Template = {
            id: responseData.id,
            name: responseData.name,
            source: responseData.source,
            webConfig: responseData.whitelist,
            status: responseData.status,
            prompts: responseData.prompts,
            fields: responseData.fields,
            docxFile: responseData.docxFile
          };

          // Update the state by creating a new array to trigger re-render
          setItems(prevItems =>
            prevItems.map(item =>
              item.id === updatedItem.id ? updatedItem : item
            )
          );
          await fetchData("Template") // Fetch data again to update the list
        }
      }
      closeDialog()

    } catch (error) {
      console.error('Error updating item:', error);
    }
  };





  // Delete item
  const deleteItem = async (index?: number) => {
    if (!index || index === undefined) {
      console.error('ID or index is missing.')
      return;
    }
    try {
      if (dataType === "Library") {
        const response = await libraryDelete(items[index].id)

        if (response.ok) {
          const responseData = await response.json()
          setItems([...items, responseData])
          await fetchData("Library") // Fetch data again to update the list
        }
      } else if (dataType === "Template") {
        const response = await templateDelete(items[index].id)

        if (response.ok) {
          const responseData = await response.json()
          setItems([...items, responseData])
          await fetchData("Template") // Fetch data again to update the list
        }
      }
      closeDialog()
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const saveItem = () => {
    if (currentItem.index !== undefined) {
      updateItem(); // Update existing item
    } else {
      createItem(); // Create new item
    }
  };

  const fetchData = async (dataType: string) => {
    switch (dataType) {
      case "General":
        const configs = await configurationList();
        setItems(configs || []);
        break;
      case "Library":
        const library = await libraryList();
        setItems(library || []);
        break;
      case "Template":
        const templateData = await templateList();
        setItems(templateData || []);
        break;
      case "Approval":
        const approvalData = await approvalList();
        setItems(approvalData || []);
        break;
      default:
        break;
    }
  };

  /* useEffect(() => {
    fetchData();
    console.log(items); // Log to check the contents
  }, [items]); */

  useEffect(() => {
    fetchData(dataType)
  }, [dataType])  // Dependency on dataType to re-fetch data if changed
  // Stopped changing here

  const [whitelists, setWhitelists] = useState<string[]>([]);

  useEffect(() => {
    async function load() {
      const list = await getWhitelists();
      setWhitelists(list);
    }
    load();
  }, []);

  const whitelistOptions = whitelists.map(w => ({
    key: w,
    text: w
  }));


  return (
    <div className={styles.container} role="main">
      <Stack horizontal className={styles.adminRoot}>
        <div className={styles.chatContainer}>
          <h2>Admin Page</h2>

          <Stack horizontal tokens={{ childrenGap: 20 }} verticalAlign="end">
            {/* Data Type Selector */}
            <Dropdown
              label="Select Configuration Type"
              options={dataOptions}
              selectedKey={dataType}
              onChange={(_, option) => setDataType(option?.key as 'General' | 'Library' | 'Template' | 'Approval')}
            />

            {(dataType === 'Library' || dataType === 'Template') && (
              <PrimaryButton text={`Add ${dataType}`} onClick={() => openDialog()} />
            )}
          </Stack>

          <div className={styles.detailsListContainer}>
            <DetailsList
              items={items.filter(item => {
                if (dataType === 'General') return item.hasOwnProperty('value')
                if (dataType === 'Library') return item.hasOwnProperty('url')
                if (dataType === 'Template') return item.hasOwnProperty('name')
                if (dataType === 'Approval') return item.hasOwnProperty('status')
                return false
              })}
              columns={getColumns()}
              selectionMode={SelectionMode.none}
              layoutMode={DetailsListLayoutMode.justified}
              getKey={item => item.id} // Ensuring a unique key for each item
            />
          </div>

          <Dialog
            hidden={!isTestDialogOpen}
            onDismiss={closeTestDialog}
            styles={{
              main: [
                {
                  selectors: {
                    ['@media (min-width: 480px)']: {
                      maxWidth: '90%',
                      background: '#FFFFFF',
                      boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      maxHeight: '100%',
                      minHeight: '90%',
                      minWidth: '70%'
                    }
                  }
                }
              ]
            }}
            dialogContentProps={{
              type: DialogType.largeHeader,
              title: 'Test Template'
            }}>
            <Stack tokens={{ childrenGap: 20 }}>
              {currentTestItem.fields
                ?.filter(field => field.type === 'dropdown') // Only include fields of type "dropdown"
                .map((field, i) => (
                  <Dropdown
                    key={field.name}
                    onChange={(event, option) => onFieldValueChange(field.name, option)}
                    placeholder={`Select ${field.name}`}
                    label={field.name}
                    options={field.mapping.list.map(opt => ({ key: opt, text: opt }))} // Assuming `list` is a string array
                  />
                ))}

              {isAllFieldsSelected && <PrimaryButton text="Generate Template Prompts" onClick={handleTestClick} />}

              {/* Display the response list */}
              {testResponseList.length > 0 && (
                <ul>
                  {testResponseList.map((item, index) => (
                    <React.Fragment key={index}>
                      {/* Conditionally render the heading outside of the <li> */}
                      {(index === 0 || item[0] !== testResponseList[index - 1][0]) && <h4>{item[0]}:</h4>}
                      {/* Show item[1] inside the <li> */}
                      <li>{item[1]}</li>
                    </React.Fragment>
                  ))}
                </ul>
              )}
            </Stack>
          </Dialog>

          {/* Dialog for Adding/Editing Items */}
          <Dialog
            hidden={!isDialogOpen}
            onDismiss={closeDialog}
            styles={{
              main: [
                {
                  selectors: {
                    ['@media (min-width: 480px)']: {
                      maxWidth: '90%',
                      background: '#FFFFFF',
                      boxShadow: '0px 14px 28.8px rgba(0, 0, 0, 0.24), 0px 0px 8px rgba(0, 0, 0, 0.2)',
                      borderRadius: '8px',
                      maxHeight: '100%',
                      minHeight: '90%',
                      minWidth: '70%'
                    }
                  }
                }
              ]
            }}
            dialogContentProps={{
              type: DialogType.largeHeader,
              title: currentItem.index !== undefined ? `Edit ${dataType}` : `Add ${dataType}`
            }}>
            {dataType === 'General' && (
              <>
                <TextField label="Name" value={(currentItem as Configuration).name || ''} disabled />
                <TextField
                  label="Description"
                  value={(currentItem as Configuration).description || ''}
                  disabled
                  multiline
                  autoAdjustHeight
                />
                <TextField
                  label="Value"
                  value={(currentItem as Configuration).value || ''}
                  onChange={(e, newValue) => setCurrentItem({ ...currentItem, value: newValue || '' })}
                  multiline
                  autoAdjustHeight
                />
              </>
            )}
            {dataType === 'Library' && (
              <>
                <TextField
                  label="Name"
                  value={(currentItem as Library).name || ''}
                  onChange={(e, newValue) => setCurrentItem({ ...currentItem, name: newValue || '' })}
                />
                <TextField
                  label="URL"
                  value={(currentItem as Library).url || ''}
                  onChange={(e, newValue) => setCurrentItem({ ...currentItem, url: newValue || '' })}
                />
              </>
            )}
            {dataType === 'Template' && (
              <>
                <TextField
                  label="Template Name"
                  value={(currentItem as Template).name || ''}
                  onChange={(e, newValue) => setCurrentItem({ ...currentItem, name: newValue || '' })}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      // Set focus to the first dropdown when Enter is pressed
                      dropdownRef.current?.focus()
                    }
                  }}
                />

                <Stack tokens={{ childrenGap: 20 }}>
                  <Stack tokens={{ childrenGap: 0 }}>
                    <Dropdown
                      componentRef={dropdownRef} // Attach the componentRef to the dropdown
                      label="Source for search (AI Search Index)"
                      selectedKey={currentItem.source} // Use selectedKey instead of defaultValue
                      onChange={(event, option) => setCurrentItem({ ...currentItem, source: option?.key as string })}
                      options={sourceOptions}
                    />

                    <Dropdown
                      label="Select Whitelist"
                      selectedKey={currentItem.webConfig}
                      onChange={(event, option) => setCurrentItem({ ...currentItem, webConfig: option?.key as string })}
                      options={whitelistOptions}
                    />
                  </Stack>
                  <Checkbox
                    label="Active for All Users"
                    checked={currentItem.status === 'Active'}
                    onChange={(_, checked) => {
                      setCurrentItem({ ...currentItem, status: checked ? 'Active' : 'Inactive' })
                    }}
                  />

                  <h3>Template Document</h3>

                  {/* Add a file upload field here */}
                  <div className={styles.fileInputContainer}>
                    <input
                      type="file"
                      id="fileInput"
                      onChange={event => handleFileUpload(event)}
                      accept="application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className={styles.fileInput}
                    />
                  </div>
                </Stack>

                {currentItem.docxFile ? (
                  <div>
                    <p>A template has already been uploaded, but you can upload a newer version.</p>
                  </div>
                ) : (
                  <div>
                    <p>A template hasn't been uploaded yet.</p>
                  </div>
                )}

                <h3>Fields</h3>

                {currentItem.fields?.map((field, i) => (
                  <React.Fragment key={i}>
                    <Stack tokens={{ childrenGap: 10 }}>
                      <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <TextField
                          label="Field Name"
                          value={field.name}
                          componentRef={el => (inputRefs.current[`field-${i}`] = el)} // Unique key
                          onChange={(_, newValue) => {
                            const updatedFields = [...currentItem.fields!]
                            updatedFields[i].name = newValue || ''
                            setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                          }}
                          onKeyDown={event => {
                            if (event.key === 'Enter') {
                              const updatedFields = [...currentItem.fields!]
                              updatedFields[i].mapping.list.push('') // Push new item to mapping list
                              setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                              setTimeout(() => {
                                inputRefs.current[`dropdown-${i}-${field.mapping.list.length - 1}`]?.focus() // Ensure focus is assigned correctly
                              }, 0)
                            }
                          }}
                        />

                        {/* Dropdown for Field Type */}
                        <Dropdown
                          label="Field Type"
                          selectedKey={field.type}
                          options={[
                            { key: 'dropdown', text: 'Dropdown' },
                            { key: 'dynamic', text: 'Dropdown Child' }
                          ]}
                          onChange={(_, option) => {
                            const updatedFields = [...currentItem.fields!]
                            updatedFields[i].type = option?.key as string // Set selected type
                            setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                          }}
                          styles={{ root: { minWidth: 200 } }} // Added margin-top
                        />

                        <IconButton
                          iconProps={{ iconName: 'Delete' }}
                          title="Delete Field"
                          onClick={() => {
                            const updatedFields = [...currentItem.fields!]
                            updatedFields.splice(i, 1)
                            setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                          }}
                          styles={{ root: { marginTop: 30 } }} // Added margin-top
                        />
                      </Stack>

                      {/* Manage dropdown options when type is "dropdown" */}
                      {field.type === 'dropdown' && (
                        <div>
                          <h5>Dropdown {field.name} Options</h5>
                          {field.mapping?.list?.map((option, idx) => (
                            <Stack key={idx} horizontal tokens={{ childrenGap: 10 }}>
                              <TextField
                                key={`dropdown-${i}-${idx}`} // Unique key per option
                                value={option}
                                componentRef={el => (inputRefs.current[`dropdown-${i}-${idx}`] = el)} // Store ref with unique key
                                onChange={(_, newValue) => {
                                  const updatedFields = [...currentItem.fields!]
                                  updatedFields[i].mapping.list[idx] = newValue || ''
                                  setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                                }}
                                onKeyDown={event => {
                                  if (event.key === 'Enter') {
                                    const updatedFields = [...currentItem.fields!]
                                    updatedFields[i].mapping.list.push('') // Push new item to mapping list
                                    setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                                    setTimeout(() => inputRefs.current[`dropdown-${i}-${idx + 1}`]?.focus(), 0)
                                  }
                                }}
                              />
                              <IconButton
                                iconProps={{ iconName: 'Delete' }}
                                title="Remove Option"
                                onClick={() => {
                                  const updatedFields = [...currentItem.fields!]
                                  updatedFields[i].mapping.list.splice(idx, 1)
                                  setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                                }}
                              />
                            </Stack>
                          ))}
                          <DefaultButton
                            text="Add Dropdown Option"
                            onClick={() => {
                              const updatedFields = [...currentItem.fields!]
                              updatedFields[i].mapping.list.push('')
                              setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                            }}
                            styles={{ root: { marginLeft: 10 } }} // Added margin-top
                          />
                        </div>
                      )}

                      {field.type === 'dynamic' && (
                        <div>
                          <h4>{field.name} Dropdown Child Mapping</h4>

                          {/* Select Parent Field Dropdown */}
                          <Dropdown
                            label="Parent Dropdown Field"
                            selectedKey={field.mapping?.parentField || ''}
                            onChange={(_, option) => {
                              if (!option) return

                              const updatedFields = [...currentItem.fields!]
                              const selectedParentField = (updatedFields[i].mapping.parentField = option.key as string)

                              // Find the parent field's options
                              const parentFieldOptions =
                                currentItem.fields?.find(f => f.name === selectedParentField)?.mapping?.list || []

                              // Automatically populate nestedFields with empty child arrays for each parent option
                              const newNestedFields = parentFieldOptions.reduce((acc, parentValue) => {
                                acc[parentValue] = acc[parentValue] || [] // Ensure existing child options remain
                                return acc
                              }, updatedFields[i].mapping.nestedFields || {})

                              updatedFields[i].mapping.nestedFields = newNestedFields
                              setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                            }}
                            options={
                              currentItem.fields
                                ?.filter(f => f.type === 'dropdown') // Only fields with type 'dropdown'
                                .map(f => ({ key: f.name, text: f.name })) || []
                            } // Use field name as key
                            placeholder="Select parent dropdown field"
                          />

                          {/* Display Child Options for the Selected Parent */}
                          {field.mapping?.nestedFields &&
                            Object.entries(field.mapping.nestedFields).map(([parentValue, children], idx) => (
                              <React.Fragment key={idx}>
                                <Stack tokens={{ childrenGap: 10 }}>
                                  <Stack horizontal tokens={{ childrenGap: 10 }}>
                                    {/* Parent Value Input */}
                                    <Dropdown
                                      label="Select Parent Dropdown Field"
                                      selectedKey={parentValue}
                                      onChange={(_, option) => {
                                        if (!option) return
                                        const updatedFields = [...currentItem.fields!]
                                        const newMapping = { ...updatedFields[i].mapping.nestedFields }

                                        // Rename the parent option key
                                        newMapping[option.key as string] = newMapping[parentValue]
                                        delete newMapping[parentValue]

                                        updatedFields[i].mapping.nestedFields = newMapping
                                        setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                                      }}
                                      options={
                                        currentItem.fields
                                          ?.find(f => f.name === field.mapping?.parentField)
                                          ?.mapping?.list?.map(option => ({
                                            key: option,
                                            text: option
                                          })) || [] // Use parent field's options as parent value dropdown options
                                      }
                                      placeholder="Select a parent value"
                                    />
                                    <IconButton
                                      iconProps={{ iconName: 'Delete' }}
                                      title="Remove Parent Option"
                                      onClick={() => {
                                        const updatedFields = [...currentItem.fields!]
                                        delete updatedFields[i].mapping.nestedFields[parentValue]
                                        setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                                      }}
                                      styles={{ root: { marginTop: 25 } }} // Added margin-top
                                    />
                                  </Stack>

                                  {/* Child Options for Selected Parent */}
                                  {children.map((child, childIdx) => (
                                    <Stack key={childIdx} horizontal tokens={{ childrenGap: 10 }}>
                                      <TextField
                                        value={child}
                                        componentRef={el => (inputRefs.current[`child-${i}-${idx}-${childIdx}`] = el)}
                                        onChange={(_, newValue) => {
                                          const updatedFields = [...currentItem.fields!]
                                          updatedFields[i].mapping.nestedFields[parentValue][childIdx] = newValue || ''
                                          setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                                        }}
                                        onKeyDown={event => {
                                          if (event.key === 'Enter') {
                                            const updatedFields = [...currentItem.fields!]
                                            updatedFields[i].mapping.nestedFields[parentValue].push('') // Push new item to mapping list
                                            setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                                            setTimeout(() => {
                                              const newIndex =
                                                updatedFields[i].mapping.nestedFields[parentValue].length - 1
                                              inputRefs.current[`child-${i}-${idx}-${newIndex}`]?.focus() // Focus on the new field
                                            }, 0)
                                          }
                                        }}
                                        placeholder="Enter Child Option"
                                      />
                                      <IconButton
                                        iconProps={{ iconName: 'Delete' }}
                                        title="Remove Child Value"
                                        onClick={() => {
                                          const updatedFields = [...currentItem.fields!]
                                          updatedFields[i].mapping.nestedFields[parentValue].splice(childIdx, 1)
                                          setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                                        }}
                                      />
                                    </Stack>
                                  ))}

                                  {/* Add Child Option */}
                                  <DefaultButton
                                    text="Add Child Value"
                                    onClick={() => {
                                      const updatedFields = [...currentItem.fields!]
                                      updatedFields[i].mapping.nestedFields[parentValue].push('')
                                      setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                                      // Focus the newly added input field
                                      setTimeout(() => {
                                        const newIndex = updatedFields[i].mapping.nestedFields[parentValue].length - 1
                                        inputRefs.current[`child-${i}-${idx}-${newIndex}`]?.focus() // Ensure focus on the newly added input
                                      }, 0)
                                    }}
                                    styles={{ root: { maxWidth: 200 } }} // Added margin-top
                                  />
                                </Stack>
                                {/* Add separator between items, but not after the last one */}
                                {field.mapping?.nestedFields &&
                                  idx < Object.keys(field.mapping.nestedFields).length && (
                                    <Separator styles={{ root: { borderWidth: '2px', borderColor: 'black' } }} />
                                  )}
                              </React.Fragment>
                            ))}

                          {
                            <DefaultButton
                              text="Add Parent Option"
                              onClick={() => {
                                const updatedFields = [...currentItem.fields!]
                                if (!updatedFields[i].mapping.nestedFields) {
                                  updatedFields[i].mapping.nestedFields = {}
                                }
                                updatedFields[i].mapping.nestedFields[''] = [] // Empty parent by default
                                setCurrentItem(prev => ({ ...prev, fields: updatedFields }))
                              }}
                            />
                          }
                        </div>
                      )}
                    </Stack>

                    {/* Add separator between items, but not after the last one */}
                    {currentItem.fields && i < currentItem.fields.length - 1 && (
                      <Separator styles={{ root: { borderWidth: '2px', borderColor: 'black' } }} />
                    )}
                  </React.Fragment>
                ))}

                <DefaultButton
                  text="Add Field"
                  onClick={() => {
                    setCurrentItem(prev => {
                      const newFields = [
                        ...(prev.fields || []),
                        {
                          name: '',
                          type: 'dropdown',
                          mapping: { list: [], nestedFields: {}, parentField: '' }
                        }
                      ]

                      // Delay focus until the field is rendered
                      setTimeout(() => {
                        const newIndex = newFields.length // Get index of new field
                        inputRefs.current[newIndex - 1]?.focus() // Focus on the new input field
                      }, 0)

                      return { ...prev, fields: newFields }
                    })
                  }}
                  styles={{ root: { marginTop: 15 } }} // Added margin-top
                />

                <h3>Chapters</h3>
                {/* Add Prompts */}
                {currentItem.prompts?.map((prompt, i) => (
                  <React.Fragment key={i}>
                    {/* Stack for each prompt item */}
                    <Stack horizontal tokens={{ childrenGap: 10 }} styles={{ root: { paddingBottom: 10 } }}>
                      <Stack grow>
                        <TextField
                          label="Title"
                          value={prompt.title}
                          componentRef={el => (inputRefs.current[`chapter-title-${i}`] = el)}
                          onChange={(_, newValue) => {
                            const updatedPrompts = [...currentItem.prompts!]
                            updatedPrompts[i].title = newValue || ''
                            setCurrentItem(prev => ({ ...prev, prompts: updatedPrompts }))
                          }}
                          onKeyDown={event => {
                            if (event.key === 'Enter') {
                              // Focus on the "Prompt" field when Enter is pressed
                              setTimeout(() => {
                                inputRefs.current[`chapter-prompt-${i}`]?.focus() // Move focus to the "Prompt" field
                              }, 0)
                            }
                          }}
                        />
                        <TextField
                          label="Prompt"
                          value={prompt.prompt}
                          componentRef={el => (inputRefs.current[`chapter-prompt-${i}`] = el)}
                          onChange={(_, newValue) => {
                            const updatedPrompts = [...currentItem.prompts!]
                            updatedPrompts[i].prompt = newValue || ''
                            setCurrentItem(prev => ({ ...prev, prompts: updatedPrompts }))
                          }}
                          onKeyDown={event => {
                            if (event.key === 'Enter') {
                              const newPrompt = { title: '', prompt: '' }
                              setCurrentItem(prev => {
                                const newPrompts = [...(prev.prompts || []), newPrompt]
                                setTimeout(() => {
                                  inputRefs.current[`chapter-title-${newPrompts.length - 1}`]?.focus() // Focus on the new Title field
                                }, 0)
                                return { ...prev, prompts: newPrompts }
                              })
                            }
                          }}
                          multiline
                          autoAdjustHeight
                        />
                      </Stack>

                      <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title="Delete Chapter"
                        onClick={() => {
                          setCurrentItem(prev => ({
                            ...prev,
                            prompts: prev.prompts?.filter((_, idx) => idx !== i)
                          }))
                        }}
                        styles={{ root: { marginTop: 40 } }} // Added margin-top
                      />
                    </Stack>

                    {/* Add separator between items, but not after the last one */}
                    {currentItem.prompts && i < currentItem.prompts.length && (
                      <Separator styles={{ root: { borderWidth: '2px', borderColor: 'black' } }} />
                    )}
                  </React.Fragment>
                ))}

                <DefaultButton
                  text="Add Chapter"
                  onClick={() => {
                    setCurrentItem(prev => {
                      const newPrompts = [...(prev.prompts || []), { title: '', prompt: '' }]
                      setTimeout(() => {
                        inputRefs.current[`chapter-title-${newPrompts.length - 1}`]?.focus() // Focus on new chapter's title
                      }, 0)
                      return { ...prev, prompts: newPrompts }
                    })
                  }}
                />
              </>
            )}
            <Stack horizontal tokens={{ childrenGap: 10, padding: 10 }}>
              <PrimaryButton text="Save" onClick={saveItem} />
              <DefaultButton text="Cancel" onClick={closeDialog} />
            </Stack>
          </Dialog>

          <Dialog
            hidden={!showDeleteAlertDialog}
            onDismiss={handleCloseDeleteAlertDialog}
            dialogContentProps={deleteAlertDialogContentProps}
            modalProps={modalProps}>
            <DialogFooter>
              <PrimaryButton
                onClick={() => {
                  if (selectedIndex !== null) {
                    deleteItem(selectedIndex)
                    handleCloseDeleteAlertDialog()
                  }
                }}
                text="Delete"
              />

              <DefaultButton onClick={handleCloseDeleteAlertDialog} text={'Cancel'} />
            </DialogFooter>
          </Dialog>
        </div>
      </Stack>
    </div>
  )
}

export default Admin
